import { Helmet } from 'react-helmet-async';
import { DashboardLayout } from '../../components/layout/DashboardLayout';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Check, Edit2, Plus, Trash2, X, Save } from 'lucide-react';
import { useState } from 'react';
import { getPlans, updatePlan, createPlan, deletePlan } from '../../services/storage';
import type { Plan } from '../../types';
import { v4 as uuidv4 } from '../../lib/uuid';

export const PlansAdmin = () => {
  const [plans, setPlans] = useState<Plan[]>(getPlans());
  const [editingPlan, setEditingPlan] = useState<string | null>(null);
  const [editedPlan, setEditedPlan] = useState<Plan | null>(null);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newPlan, setNewPlan] = useState<Partial<Plan>>({
    name: '',
    category: '',
    description: '',
    monthlyPrice: 0,
    yearlyPrice: 0,
    maxUsers: 100,
    employeeCount: '',
    adminAccess: '',
    sectionTitle: '',
    features: [],
    isPopular: false,
    buttonVariant: 'outline',
    yearlyDiscountEnabled: false
  });

  const handleEdit = (plan: Plan) => {
    setEditingPlan(plan.id);
    setEditedPlan({ ...plan });
  };

  const handleSave = () => {
    if (editedPlan) {
      const updated = updatePlan(editedPlan.id, editedPlan);
      if (updated) {
        setPlans(getPlans());
        setEditingPlan(null);
        setEditedPlan(null);
      }
    }
  };

  const handleCancel = () => {
    setEditingPlan(null);
    setEditedPlan(null);
  };

  const handleDelete = (id: string) => {
    if (confirm('Tem certeza que deseja excluir este plano?')) {
      deletePlan(id);
      setPlans(getPlans());
    }
  };

  const handleCreate = () => {
    const plan: Plan = {
      id: uuidv4(),
      name: newPlan.name || '',
      category: newPlan.category || '',
      description: newPlan.description || '',
      monthlyPrice: newPlan.monthlyPrice || 0,
      yearlyPrice: newPlan.yearlyPrice || 0,
      maxUsers: newPlan.maxUsers || 100,
      employeeCount: newPlan.employeeCount || '',
      adminAccess: newPlan.adminAccess || '',
      sectionTitle: newPlan.sectionTitle || '',
      features: newPlan.features || [],
      isPopular: newPlan.isPopular || false,
      buttonVariant: newPlan.buttonVariant || 'outline',
      yearlyDiscountEnabled: newPlan.yearlyDiscountEnabled || false
    };
    createPlan(plan);
    setPlans(getPlans());
    setShowCreateForm(false);
    setNewPlan({
      name: '',
      category: '',
      description: '',
      monthlyPrice: 0,
      yearlyPrice: 0,
      maxUsers: 100,
      employeeCount: '',
      adminAccess: '',
      sectionTitle: '',
      features: [],
      isPopular: false,
      buttonVariant: 'outline'
    });
  };

  const handleFeatureChange = (planId: string, index: number, value: string) => {
    if (editedPlan && editedPlan.id === planId) {
      const newFeatures = [...editedPlan.features];
      newFeatures[index] = value;
      setEditedPlan({ ...editedPlan, features: newFeatures });
    }
  };

  const handleAddFeature = (planId: string) => {
    if (editedPlan && editedPlan.id === planId) {
      setEditedPlan({ ...editedPlan, features: [...editedPlan.features, ''] });
    }
  };

  const handleRemoveFeature = (planId: string, index: number) => {
    if (editedPlan && editedPlan.id === planId) {
      const newFeatures = editedPlan.features.filter((_, i) => i !== index);
      setEditedPlan({ ...editedPlan, features: newFeatures });
    }
  };

  return (
    <DashboardLayout>
      <Helmet>
        <title>Planos | Portal Ethics</title>
      </Helmet>

      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-neutral-900">Planos</h1>
            <p className="text-neutral-600">Gerencie os planos disponíveis</p>
          </div>
          <Button 
            variant="primary" 
            className="flex items-center gap-2"
            onClick={() => setShowCreateForm(true)}
          >
            <Plus className="w-4 h-4" />
            Novo Plano
          </Button>
        </div>

        {showCreateForm && (
          <Card className="p-6 border-2 border-primary-blue">
            <h3 className="text-lg font-bold text-neutral-900 mb-4">Criar Novo Plano</h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Nome</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-blue"
                  value={newPlan.name}
                  onChange={(e) => setNewPlan({ ...newPlan, name: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Categoria</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-blue"
                  value={newPlan.category}
                  onChange={(e) => setNewPlan({ ...newPlan, category: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Descrição</label>
                <textarea
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-blue"
                  value={newPlan.description}
                  onChange={(e) => setNewPlan({ ...newPlan, description: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Preço Mensal</label>
                  <input
                    type="number"
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-blue"
                    value={newPlan.monthlyPrice}
                    onChange={(e) => setNewPlan({ ...newPlan, monthlyPrice: parseFloat(e.target.value) })}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-neutral-700 mb-1">Preço Anual</label>
                  <input
                    type="number"
                    className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-blue"
                    value={newPlan.yearlyPrice}
                    onChange={(e) => setNewPlan({ ...newPlan, yearlyPrice: parseFloat(e.target.value) })}
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Máximo de Usuários</label>
                <input
                  type="number"
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-blue"
                  value={newPlan.maxUsers}
                  onChange={(e) => setNewPlan({ ...newPlan, maxUsers: parseInt(e.target.value) })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Contagem de Funcionários</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-blue"
                  value={newPlan.employeeCount}
                  onChange={(e) => setNewPlan({ ...newPlan, employeeCount: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Acesso Administrativo</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-blue"
                  value={newPlan.adminAccess}
                  onChange={(e) => setNewPlan({ ...newPlan, adminAccess: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-neutral-700 mb-1">Título da Seção</label>
                <input
                  type="text"
                  className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-blue"
                  value={newPlan.sectionTitle}
                  onChange={(e) => setNewPlan({ ...newPlan, sectionTitle: e.target.value })}
                />
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="isPopular"
                  checked={newPlan.isPopular}
                  onChange={(e) => setNewPlan({ ...newPlan, isPopular: e.target.checked })}
                />
                <label htmlFor="isPopular" className="text-sm font-medium text-neutral-700">Plano Popular</label>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="yearlyDiscountEnabled"
                  checked={newPlan.yearlyDiscountEnabled}
                  onChange={(e) => setNewPlan({ ...newPlan, yearlyDiscountEnabled: e.target.checked })}
                />
                <label htmlFor="yearlyDiscountEnabled" className="text-sm font-medium text-neutral-700">Ativar Desconto Anual (-20%)</label>
              </div>
              <div className="flex gap-2">
                <Button variant="primary" onClick={handleCreate}>Criar</Button>
                <Button variant="outline" onClick={() => setShowCreateForm(false)}>Cancelar</Button>
              </div>
            </div>
          </Card>
        )}

        {/* Plans Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {plans.map((plan) => (
            <Card key={plan.id} className={`p-6 ${plan.isPopular ? 'border-2 border-primary-blue' : ''}`}>
              {editingPlan === plan.id ? (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">Nome</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-blue"
                      value={editedPlan?.name || ''}
                      onChange={(e) => setEditedPlan({ ...editedPlan!, name: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">Categoria</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-blue"
                      value={editedPlan?.category || ''}
                      onChange={(e) => setEditedPlan({ ...editedPlan!, category: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">Descrição</label>
                    <textarea
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-blue"
                      value={editedPlan?.description || ''}
                      onChange={(e) => setEditedPlan({ ...editedPlan!, description: e.target.value })}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Preço Mensal</label>
                      <input
                        type="number"
                        className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-blue"
                        value={editedPlan?.monthlyPrice || 0}
                        onChange={(e) => setEditedPlan({ ...editedPlan!, monthlyPrice: parseFloat(e.target.value) })}
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-1">Preço Anual</label>
                      <input
                        type="number"
                        className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-blue"
                        value={editedPlan?.yearlyPrice || 0}
                        onChange={(e) => setEditedPlan({ ...editedPlan!, yearlyPrice: parseFloat(e.target.value) })}
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">Máximo de Usuários</label>
                    <input
                      type="number"
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-blue"
                      value={editedPlan?.maxUsers || 0}
                      onChange={(e) => setEditedPlan({ ...editedPlan!, maxUsers: parseInt(e.target.value) })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">Contagem de Funcionários</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-blue"
                      value={editedPlan?.employeeCount || ''}
                      onChange={(e) => setEditedPlan({ ...editedPlan!, employeeCount: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">Acesso Administrativo</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-blue"
                      value={editedPlan?.adminAccess || ''}
                      onChange={(e) => setEditedPlan({ ...editedPlan!, adminAccess: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1">Título da Seção</label>
                    <input
                      type="text"
                      className="w-full px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-blue"
                      value={editedPlan?.sectionTitle || ''}
                      onChange={(e) => setEditedPlan({ ...editedPlan!, sectionTitle: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">Funcionalidades</label>
                    <div className="space-y-2">
                      {(editedPlan?.features || []).map((feature, idx) => (
                        <div key={idx} className="flex gap-2">
                          <input
                            type="text"
                            className="flex-1 px-3 py-2 border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-blue"
                            value={feature}
                            onChange={(e) => handleFeatureChange(plan.id, idx, e.target.value)}
                          />
                          <Button
                            variant="outline"
                            className="px-2 text-red-600"
                            onClick={() => handleRemoveFeature(plan.id, idx)}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => handleAddFeature(plan.id)}
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        Adicionar Funcionalidade
                      </Button>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id={`popular-${plan.id}`}
                      checked={editedPlan?.isPopular || false}
                      onChange={(e) => setEditedPlan({ ...editedPlan!, isPopular: e.target.checked })}
                    />
                    <label htmlFor={`popular-${plan.id}`} className="text-sm font-medium text-neutral-700">Plano Popular</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id={`yearlyDiscount-${plan.id}`}
                      checked={editedPlan?.yearlyDiscountEnabled || false}
                      onChange={(e) => setEditedPlan({ ...editedPlan!, yearlyDiscountEnabled: e.target.checked })}
                    />
                    <label htmlFor={`yearlyDiscount-${plan.id}`} className="text-sm font-medium text-neutral-700">Ativar Desconto Anual (-20%)</label>
                  </div>
                  <div className="flex gap-2">
                    <Button variant="primary" className="flex-1" onClick={handleSave}>
                      <Save className="w-4 h-4 mr-2" />
                      Salvar
                    </Button>
                    <Button variant="outline" className="flex-1" onClick={handleCancel}>
                      <X className="w-4 h-4 mr-2" />
                      Cancelar
                    </Button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <h3 className="text-lg font-bold text-neutral-900">{plan.name}</h3>
                      <p className="text-sm text-neutral-600">{plan.description}</p>
                    </div>
                    {plan.isPopular && (
                      <Badge variant="primary">Popular</Badge>
                    )}
                  </div>

                  <div className="mb-6">
                    <div className="flex items-baseline">
                      <span className="text-3xl font-bold text-neutral-900">
                        {new Intl.NumberFormat('pt-BR', {
                          style: 'currency',
                          currency: 'BRL',
                          minimumFractionDigits: 0,
                          maximumFractionDigits: 0
                        }).format(plan.monthlyPrice)}
                      </span>
                      <span className="text-neutral-500 ml-1">/mês</span>
                    </div>
                    <p className="text-sm text-neutral-500 mt-1">
                      {new Intl.NumberFormat('pt-BR', {
                        style: 'currency',
                        currency: 'BRL',
                        minimumFractionDigits: 0,
                        maximumFractionDigits: 0
                      }).format(plan.yearlyPrice)}/ano {plan.yearlyDiscountEnabled && '(economize 20%)'}
                    </p>
                  </div>

                  <div className="space-y-2 mb-6">
                    <div className="flex items-center gap-2 text-sm text-neutral-600">
                      <Check className="w-4 h-4 text-success flex-shrink-0" />
                      <span>Até {plan.maxUsers === 999999 ? 'usuários ilimitados' : `${plan.maxUsers} usuários`}</span>
                    </div>
                    {plan.features.map((feature, idx) => (
                      <div key={idx} className="flex items-center gap-2 text-sm text-neutral-600">
                        <Check className="w-4 h-4 text-success flex-shrink-0" />
                        <span>{feature}</span>
                      </div>
                    ))}
                  </div>

                  <div className="flex gap-2">
                    <Button variant="outline" className="flex-1 flex items-center justify-center gap-2" onClick={() => handleEdit(plan)}>
                      <Edit2 className="w-4 h-4" />
                      Editar
                    </Button>
                    <Button variant="outline" className="px-3 text-red-600 hover:bg-red-50" onClick={() => handleDelete(plan.id)}>
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </>
              )}
            </Card>
          ))}
        </div>
      </div>
    </DashboardLayout>
  );
};